// Interpret the sendCampaign 409 "confirmation gate" estimate into a UI decision.
//
// The server can raise TWO independent confirmation gates on a campaign send:
//   • large SEGMENT volume (totalSegments over the threshold), and
//   • any over-allowance CHARGE (monthToDate.overageCostUsd > 0).
// The charge is the one that needs explicit dollar consent from the customer. When
// it's present we lead with the cost-approval dialog; because we echo BOTH totals on
// resend, approving the charge also clears the volume gate in a single follow-up send.
//
// Pure + import-free on purpose so it unit-tests directly (mirrored in
// cypress/node-tests/campaign-gate.test.js — KEEP IN SYNC).
//
// KEEP IN SYNC WITH: campaign-service lib/segment-gate.js (the server side of this gate).

export function interpretSendGate(estimate, wallet, walletChannels) {
  const e = estimate || {};
  const mtd = e.monthToDate || null;
  const overageAmount = Math.max(0, Number(mtd && mtd.overageCostUsd) || 0);
  // chargeAmount = what the practice is actually billed: overageAmount floored to
  // Stripe's $0.50 minimum (server-computed as monthToDate.chargeUsd). A sub-$0.50
  // charge is rejected by Stripe and would block the send, so we round up. Fall back
  // to overageAmount if an older server didn't send chargeUsd. This is the amount we
  // DISPLAY and the amount the customer CONSENTS to (echoed on resend).
  const legacyChargeAmount = Math.max(0, Number(mtd && mtd.chargeUsd) || overageAmount);
  const totalSegments = Math.max(0, Number(e.totalSegments) || 0);

  // E3 MULTI-POOL consent: when the server sends a `walletChannels` array (one consent object
  // per channel — sms, email) the customer approves a COMBINED credit top-up across pools. Each
  // channel mirrors the single-`wallet` shape; the total is the sum of per-channel top-ups and
  // the resend echoes THAT combined total (the server accepts >= the combined shortfall). When
  // `walletChannels` is absent we fall through to the single-`wallet` / legacy path below,
  // byte-identical to before (so every existing test + the SMS-only flow are untouched).
  if (Array.isArray(walletChannels) && walletChannels.length > 0) {
    const channels = walletChannels.map((c) => {
      const ch = c || {};
      const shortfallUsd = Math.max(0, Number(ch.shortfallUsd) || 0);
      // reloadAmountUsd = the bulk amount actually charged for this pool; fall back to the
      // shortfall for an older server that only sized the raw amount.
      const topUpUsd = Math.max(0, Number(ch.reloadAmountUsd) || shortfallUsd || 0);
      return {
        product: ch.product || 'sms',
        shortfallUsd,
        topUpUsd,
        appliedUsd: Math.max(0, Number(ch.appliedUsd) || shortfallUsd || 0),
        rolloverUsd: Math.max(0, Number(ch.rolloverUsd) || 0),
        freeRemainingCredits: Math.max(0, Number(ch.freeRemainingCredits) || 0),
        paidBalanceUsd: Math.max(0, Number(ch.paidBalanceUsd) || 0),
        batchCredits: Math.max(0, Number(ch.batchCredits) || 0),
      };
    });
    const totalTopUpUsd = channels.reduce((s, c) => s + c.topUpUsd, 0);
    const totalShortfallUsd = channels.reduce((s, c) => s + c.shortfallUsd, 0);
    return {
      hasOverage: totalTopUpUsd > 0,
      overageAmount: totalShortfallUsd,
      chargeAmount: totalTopUpUsd,
      minimumApplied: false,
      usingWallet: true,
      multiChannel: true,
      // Per-channel numbers so the modal can itemize (SMS $X, Email $Y) + the combined total.
      channels,
      totalTopUpUsd,
      totalShortfallUsd,
      // No single `wallet` object in multi mode — the modal reads `channels`. Kept null so the
      // single-channel modal branch isn't accidentally taken.
      wallet: null,
      chargeableSegments: Math.max(0, Number(mtd && mtd.chargeableSegments) || 0),
      totalSegments,
      recipients: Math.max(0, Number(e.recipients) || 0),
      perRecipientSegments: Math.max(1, Number(e.perRecipientSegments) || 1),
      segmentsUsed: mtd ? (Number(mtd.segmentsUsed) || 0) : null,
      allowance: mtd ? (Number(mtd.allowance) || 0) : null,
      projectedSegments: mtd ? (Number(mtd.projectedSegments) || 0) : null,
      confirmEcho: {
        confirmedTotalSegments: totalSegments,
        // The combined top-up across pools — the server accepts it since it is >= the combined
        // freshly-priced shortfall; a shortfall that grew re-triggers the ack.
        confirmedOverageAmount: totalTopUpUsd,
      },
    };
  }

  // P4c credit-wallet consent: when the server sends a `wallet` object the customer consents to a
  // BULK credit top-up (reloadAmountUsd — whole reload packs covering the shortfall, the SAME unit
  // auto-reload charges), NOT the raw micro-shortfall and NOT the legacy allowance-overage. The
  // campaign then draws only what it needs (appliedUsd); the rest (rolloverUsd) stays on the
  // balance for future sends. We never micro-charge the card. Backward-compatible: no `wallet` →
  // legacy behavior, byte-identical.
  const w = wallet || null;
  const usingWallet = !!w && (w.shortfallUsd != null || w.reloadAmountUsd != null);
  const walletShortfall = usingWallet ? Math.max(0, Number(w.shortfallUsd) || 0) : null;
  // reloadAmountUsd = the bulk amount actually charged; fall back to the shortfall for an older server.
  const walletTopUp = usingWallet ? Math.max(0, Number(w.reloadAmountUsd) || walletShortfall || 0) : null;
  const chargeAmount = usingWallet ? walletTopUp : legacyChargeAmount;
  const minimumApplied = usingWallet ? false : !!(mtd && mtd.minimumApplied);

  return {
    // which confirmation the UI must show
    hasOverage: chargeAmount > 0,
    // Single-pool / legacy path never itemizes — the modal reads `wallet` (or the legacy
    // overage copy). Present so callers can pass these through uniformly with the multi path.
    multiChannel: false,
    channels: [],
    totalTopUpUsd: chargeAmount,
    totalShortfallUsd: usingWallet ? walletShortfall : overageAmount,
    // overageAmount = the honest per-segment/shortfall math (for the breakdown); chargeAmount =
    // what the card is actually charged (the bulk top-up in wallet mode; the $0.50-floored overage
    // in legacy mode); minimumApplied when the legacy path floored.
    overageAmount: usingWallet ? walletShortfall : overageAmount,
    chargeAmount,
    minimumApplied,
    usingWallet,
    // Wallet consent context for the dialog (null on the legacy allowance path). The card is charged
    // reloadAmountUsd (bulk); appliedUsd covers this campaign now; rolloverUsd stays on the balance.
    wallet: usingWallet ? {
      freeRemainingCredits: Math.max(0, Number(w.freeRemainingCredits) || 0),
      paidBalanceUsd: Math.max(0, Number(w.paidBalanceUsd) || 0),
      batchCredits: Math.max(0, Number(w.batchCredits) || 0),
      shortfallUsd: walletShortfall,
      reloadAmountUsd: walletTopUp,
      appliedUsd: Math.max(0, Number(w.appliedUsd) || walletShortfall || 0),
      rolloverUsd: Math.max(0, Number(w.rolloverUsd) || 0),
    } : null,
    // over-allowance "message credits" the customer is actually billed for
    chargeableSegments: Math.max(0, Number(mtd && mtd.chargeableSegments) || 0),
    // context for the message
    totalSegments,
    recipients: Math.max(0, Number(e.recipients) || 0),
    perRecipientSegments: Math.max(1, Number(e.perRecipientSegments) || 1),
    // month-to-date usage is counted in SEGMENTS (credits), not texts
    segmentsUsed: mtd ? (Number(mtd.segmentsUsed) || 0) : null,
    allowance: mtd ? (Number(mtd.allowance) || 0) : null,
    projectedSegments: mtd ? (Number(mtd.projectedSegments) || 0) : null,
    // Echo BOTH acks on the resend → clears the volume gate AND the cost gate at once.
    // Echo the CHARGED amount (what the customer approved) — the server accepts it since
    // chargeAmount >= the freshly-priced overage/shortfall; a charge that grew re-triggers the ack.
    confirmEcho: {
      confirmedTotalSegments: totalSegments,
      confirmedOverageAmount: chargeAmount,
    },
  };
}

// Confirmation prompt for the VOLUME gate — a large send that is still fully
// covered by the plan (no incurred charge). Kept here (not in the Vue components)
// so it unit-tests directly and both send entry points read identically, and so
// we never quote a scary "cost" for a campaign the customer won't be billed for.
export function buildVolumeConfirmMessage(g) {
  const credits = Math.max(0, Number(g && g.totalSegments) || 0);
  const per = Math.max(1, Number(g && g.perRecipientSegments) || 1);
  const recipients = Math.max(0, Number(g && g.recipients) || 0);

  let msg =
    `This campaign will send about ${credits.toLocaleString()} message credits ` +
    `(${per} credit${per === 1 ? '' : 's'} per text × ${recipients.toLocaleString()} recipients).`;

  if (g && g.allowance != null) {
    msg +=
      ` You've used ${Number(g.segmentsUsed || 0).toLocaleString()} of your ` +
      `${Number(g.allowance || 0).toLocaleString()} free monthly credits; this campaign takes you to ` +
      `${Number(g.projectedSegments || 0).toLocaleString()} — still within your free monthly credits, no added cost.`;
  }

  msg += ' Do you want to send it?';
  return msg;
}

const fmtUsd = (n) => `$${Math.max(0, Number(n) || 0).toFixed(2)}`;
const channelLabel = (p) => (p === 'email' ? 'Email' : p === 'sms' ? 'SMS' : String(p || 'Credits'));

// SMS-only bulk-top-up wording. Kept as its own helper so BOTH the legacy single-`wallet` object
// AND a multi-pool gate that collapses to a single sms channel render byte-identical copy.
function buildSmsOnlyConsentCopy(w) {
  const topUp = Math.max(0, Number(w.reloadAmountUsd != null ? w.reloadAmountUsd : w.topUpUsd) || 0);
  const applied = Math.max(0, Number(w.appliedUsd) || 0);
  const rollover = Math.max(0, Number(w.rolloverUsd) || 0);
  return {
    title: 'Add SMS credits to send',
    lead:
      `You're out of SMS credits. To send this campaign we'll add ${fmtUsd(topUp)} of SMS credits ` +
      'to your account.',
    // Only show the split when some of the top-up actually rolls forward (a single pack that this
    // campaign fully consumes has nothing to roll over — don't imply a leftover that isn't there).
    breakdown: rollover > 0
      ? `${fmtUsd(applied)} covers this campaign; ${fmtUsd(rollover)} stays on your balance for future messages.`
      : `This covers this campaign; any remaining credits stay on your balance for future messages.`,
    checkbox: `I approve adding ${fmtUsd(topUp)} of SMS credits.`,
  };
}

// Customer-facing copy for the BULK credit top-up consent (the wallet path). We NEVER micro-charge
// the card: when a practice is out of credits, sending buys a whole reload pack (the same amount
// auto-reload uses) and the campaign draws only what it needs, the rest rolling forward. Pure +
// import-free so the OverageApprovalModal and the unit test share ONE source of truth for the wording
// and the money is disclosed honestly (the real bulk amount, not the raw shortfall).
//
// Accepts EITHER:
//   • the single-pool `wallet` object (today) → SMS-only wording, byte-identical; or
//   • the MULTI-POOL shape `{ channels: [{ product, topUpUsd, ... }], totalTopUpUsd }` → names
//     each channel + the combined total. A single sms channel collapses to the SMS-only wording
//     (so nothing changes when only SMS needs a top-up).
export function buildWalletConsentCopy(input) {
  const c = input || {};

  if (Array.isArray(c.channels)) {
    const chans = c.channels.filter((x) => x && (Number(x.topUpUsd) || 0) > 0);
    const onlySms = chans.length === 1 && chans[0].product === 'sms';
    if (chans.length > 0 && !onlySms) {
      const total = Math.max(
        0,
        Number(c.totalTopUpUsd) || chans.reduce((s, x) => s + (Number(x.topUpUsd) || 0), 0),
      );
      const names = chans.map((x) => `${channelLabel(x.product)} ${fmtUsd(x.topUpUsd)}`).join(', ');
      return {
        title: 'Add credits to send',
        lead:
          `You're out of credits. To send this campaign we'll add ${names} — ${fmtUsd(total)} total — ` +
          'of credits to your account.',
        breakdown:
          'Each pool draws only what this campaign needs; any remaining credits stay on your ' +
          'balance for future messages.',
        checkbox: `I approve adding ${fmtUsd(total)} of credits.`,
        // Per-channel line items for the modal to render (SMS: $X, Email: $Y).
        channels: chans.map((x) => ({
          product: x.product,
          label: channelLabel(x.product),
          topUpUsd: Math.max(0, Number(x.topUpUsd) || 0),
        })),
      };
    }
    // Zero or a single sms channel → SMS-only wording (using that channel's numbers when present).
    return buildSmsOnlyConsentCopy(chans[0] || {});
  }

  // Single-pool `wallet` object (today) — byte-identical.
  return buildSmsOnlyConsentCopy(c);
}

// Message for a 402 "payment required" response — adding the credits this campaign needs
// failed, so the send was blocked. Pure + testable; reassures that nothing was sent or charged.
// The server may include `product` ('sms' | 'email') on the 402 → name the channel; absent (or
// 'sms') keeps the original SMS wording, byte-identical.
export function buildPaymentFailedMessage(data) {
  const d = data || {};
  const amount = Math.max(0, Number(d.amountUsd) || 0);
  const channel = d.product === 'email' ? 'email' : 'SMS';
  const reasonText = {
    no_customer: "billing isn't set up for this practice yet",
    customer_deleted: 'the billing account on file is no longer valid',
    no_card: "there's no card on file",
    declined: 'the card on file was declined',
    unpaid: "the payment couldn't be completed",
    invalid_amount: 'the amount was invalid',
    error: "the payment couldn't be processed",
  }[d.reason] || "the payment couldn't be processed";

  return (
    `This campaign is beyond your remaining ${channel} credits, so it needs $${amount.toFixed(2)} of credits added — ` +
    `but ${reasonText}. Please update the card on file (or contact Teamio support) and try again. ` +
    'Your campaign was NOT sent, and you were NOT charged.'
  );
}
